import { PButton } from "@porsche-design-system/components-react"
import { generateListingDescriptionRoute, updateListingDescriptionRoute } from "@slfinrtl/ui-bff-routes"
import type {
	ListingDescriptionEditView,
	ListingDescriptionValues,
	ListingDescriptionView,
} from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { ButtonGroup, ListingDescriptionForm, useListingDescriptionFields } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBffDynamic, useConfig } from "src/hooks"

interface ListingDescriptionFormContainerProps {
	editView: ListingDescriptionEditView
	inventoryId: string
	onCancel: () => void
	onSaved: (view: ListingDescriptionView) => void
}

export const ListingDescriptionFormContainer = withErrorHandler(
	({ editView, inventoryId, onCancel, onSaved }: ListingDescriptionFormContainerProps) => {
		const [isSaving, setIsSaving] = useState(false)
		const { aiListingDescriptionEnabled } = useConfig()

		const form = useForm<ListingDescriptionValues>({
			defaultValues: editView.values,
			mode: "onChange",
		})
		const { handleSubmit, setValue } = form

		const fields = useListingDescriptionFields(false)
		const patch = useBffDynamic(updateListingDescriptionRoute, "inline")

		const onSubmit = useCallback(
			(values: ListingDescriptionValues) => {
				setIsSaving(true)
				return patch({
					body: values,
					pathParams: { inventoryId },
					queryParams: {},
				})
					.then(onSaved)
					.finally(() => setIsSaving(false))
			},
			[inventoryId, onSaved, patch],
		)
		const generateListingDescription = useBffDynamic(generateListingDescriptionRoute, "inline")

		const onGenerateAiListingDescriptionClick = useCallback(() => {
			return generateListingDescription({
				body: undefined,
				pathParams: { inventoryId },
				queryParams: {},
			}).then((response) => {
				setValue("listingDescription.de-DE", response.description, {
					shouldDirty: true,
					shouldValidate: true,
				})
			})
		}, [inventoryId, generateListingDescription, setValue])

		return (
			<FormProvider {...form}>
				<ErrorNotificationInline />
				<ListingDescriptionForm
					fields={fields}
					onGenerateAiListingDescriptionClick={
						aiListingDescriptionEnabled ? onGenerateAiListingDescriptionClick : undefined
					}
				/>

				<ButtonGroup>
					<PButton loading={isSaving} onClick={handleSubmit(onSubmit)}>
						<FormattedMessage {...formMessages.save} />
					</PButton>
					<PButton type="button" variant="secondary" onClick={onCancel}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				</ButtonGroup>
			</FormProvider>
		)
	},
)
